import { useId } from 'react';
import type { ReactNode } from 'react';

/**
 * Accessible switch. Rendered as a native checkbox under the hood so it
 * inherits browser/form behavior, then styled via peer-checked classes.
 * `role="switch"` is set on the visible track so AT announces "on/off",
 * not "checked/unchecked".
 *
 * Visuals mirror the Figma Switch component (Account page node 362:1163):
 * #3d2f45 track that does NOT change color on checked, and a handle that
 * slides right and recolors red (#ff0048) when on.
 */
type ToggleProps = {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: ReactNode;
  /** Set true when the user MUST flip this on to submit. Renders a small
   *  red asterisk after the label and prevents the surrounding form from
   *  submitting until checked (consumer handles the validation). */
  required?: boolean;
  disabled?: boolean;
  /** Optional helper-text shown beneath the label. */
  description?: ReactNode;
  className?: string;
};

export function Toggle({
  checked,
  onChange,
  label,
  required,
  disabled,
  description,
  className,
}: ToggleProps) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={[
        'flex w-full cursor-pointer items-start gap-4',
        disabled ? 'opacity-60 cursor-not-allowed' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <input
        type="checkbox"
        id={id}
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
        aria-required={required}
      />
      <span
        aria-hidden="true"
        className={[
          'mt-[2px] inline-flex h-[26px] w-[48px] shrink-0 items-center rounded-full bg-[#3d2f45] p-[3px]',
          // No transition / no checked-state recolor on the track — the track
          // is meant to stay #3d2f45 in both states per Figma. Only the
          // handle changes (transform + color) when checked.
          'peer-focus-visible:ring-2 peer-focus-visible:ring-white peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[#3a2f44]',
        ].join(' ')}
      >
        <span
          className={[
            'h-[20px] w-[20px] rounded-full transition-all',
            checked ? 'translate-x-[22px] bg-[#ff0048]' : 'translate-x-0 bg-[#9b7fb5]',
          ].join(' ')}
        />
      </span>
      {/* Labels associated via htmlFor toggle the input when clicked. That
          includes clicks that bubble up from nested <a>/<button> elements
          (Toggle label content often embeds Terms/Privacy/Rules links).
          Without this guard, clicking such a link both navigates AND
          unexpectedly toggles the consent. Swallow the bubble when the
          click originated on an interactive descendant so the nested
          element's own behavior is the only thing that runs. The onClick is
          a propagation guard, not an interaction — hence the lint opt-out. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <span
        className="flex flex-col gap-[2px]"
        onClick={(e) => {
          if (e.target instanceof HTMLElement && e.target.closest('a, button')) {
            e.stopPropagation();
          }
        }}
      >
        <span className="font-['Arial',sans-serif] text-[15px] leading-[1.25] text-white">
          {label}
          {required && (
            <span aria-hidden="true" className="ml-1 text-[#ff4479]">
              *
            </span>
          )}
          {required && <span className="sr-only"> (required)</span>}
        </span>
        {description && (
          <span className="font-['Arial',sans-serif] text-[13px] leading-[1.3] text-white/70">
            {description}
          </span>
        )}
      </span>
    </label>
  );
}
